import { collection, query, onSnapshot, doc, getDoc, setDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { db } from './firebase-init';
import { justDate, isoDateString } from '../utils/date-utils';

class CalendarDayNoteService {
  constructor() {
    this.dayNotes = {}; // Map of ISO date strings to note objects
    this.listeners = [];
    this.unsubscribe = null;
  }

  // Start real-time listener
  startListener(callback) {
    if (this.unsubscribe) {
      console.warn('CalendarDayNoteService listener already active');
      return;
    }

    const q = query(collection(db, 'calendarDayNotes'));

    this.unsubscribe = onSnapshot(q, (snapshot) => {
      this.dayNotes = {};
      
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        this.dayNotes[doc.id] = {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
        };
      });

      
      // Notify all listeners
      this.listeners.forEach(cb => cb(this.dayNotes));
      if (callback) callback(this.dayNotes);
    }, (error) => {
      console.error('❌ CalendarDayNoteService listener error:', error);
    });
  }

  // Stop listener
  stopListener() {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }

  // Subscribe to day note updates
  subscribe(callback) {
    this.listeners.push(callback);
    // Immediately call with current data
    callback(this.dayNotes);
    
    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    };
  }

  // Get all day notes
  getDayNotes() {
    return this.dayNotes;
  }

  // Get note for a specific date
  getNoteForDate(date) {
    const dateStr = isoDateString(justDate(date));
    return this.dayNotes[dateStr] || null;
  }

  // Set/update note for a date (simple version - just text)
  async setNoteForDate(date, noteText) {
    const dateStr = isoDateString(justDate(date));
    const now = new Date();
    
    const docRef = doc(db, 'calendarDayNotes', dateStr);
    
    // Convert dateStr to Date object for Timestamp
    const dateObj = new Date(dateStr + 'T00:00:00');

    const data = {
      date: Timestamp.fromDate(dateObj),
      text: noteText.trim(),
      audience: 'internal',
      includeIn4amEmail: true,
      updatedAt: Timestamp.fromDate(now),
    };

    await setDoc(docRef, data, { merge: true });
  }

  // Save/update note for a date (with full note data object)
  async saveDayNote(dateStr, noteData) {
    const now = new Date();
    
    const docRef = doc(db, 'calendarDayNotes', dateStr);
    
    // Convert dateStr to Date object for Timestamp
    const dateObj = new Date(dateStr + 'T00:00:00');
    
    const data = {
      date: Timestamp.fromDate(dateObj),
      text: noteData.text || '',
      audience: noteData.audience || 'internal',
      includeIn4amEmail: noteData.includeIn4amEmail !== false,
      updatedAt: Timestamp.fromDate(now),
    };
    
    await setDoc(docRef, data, { merge: true });
  }

  // Delete note for a date (accepts Date object)
  async deleteNoteForDate(date) {
    const dateStr = isoDateString(justDate(date));
    const docRef = doc(db, 'calendarDayNotes', dateStr);
    
    await deleteDoc(docRef);
  }

  // Delete note for a date (accepts dateStr directly)
  async deleteDayNote(dateStr) {
    const docRef = doc(db, 'calendarDayNotes', dateStr);
    await deleteDoc(docRef);
  }

  // Check if date has a note
  hasNoteForDate(date) {
    const dateStr = isoDateString(justDate(date));
    return !!this.dayNotes[dateStr];
  }
}

// Export singleton instance
export const calendarDayNoteService = new CalendarDayNoteService();
export default calendarDayNoteService;